import React, { useCallback, useState } from "react";

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation } from "@apollo/client";
import { toast } from "react-toastify";

import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import useToggle from "react-use/lib/useToggle";

import { QUERY_EMPLOYEES_COUNT } from "../../config/graphql/query";
import { MUTATION_DELETE_COMPANY_EMPLOYEES } from "../../config/graphql/mutation";

type ActiveSync = {
  source: string;
};

const SOURCE_TITLE_KEY: Record<string, string> = {
  ActiveDirectory: "company:integrations.activeDirectory.section.title",
  GoogleWorkspace: "company:integrations.googleWorkspace.section.title",
};

/**
 * The company is synchronized. Removing the integration together with the
 * employees it brought in is the existing disconnect flow, which also copes with
 * employees fed by more than one sync — so point the admin there rather than
 * offering a second way to do it here.
 */
const SyncBlockedBody: React.FC<{ companyId: string; sync: ActiveSync }> =
  React.memo(({ companyId, sync }) => {
    const { t } = useTranslation(["company", "common"]);

    /**
     * The Integrations tab of the company — that is where the sync is listed with
     * its Remove action, and the only place where "keep employees" is honoured.
     * The deeper /integrations/activedirectory routes are the create/edit form.
     */
    const integrationsPath = `/company/${companyId}/integrations`;

    const sourceTitleKey = SOURCE_TITLE_KEY[sync.source];

    return (
      <>
        <p>
          {t("company:companies.modal.deleteEmployees.syncBlocked.body", {
            source: sourceTitleKey ? t(sourceTitleKey) : sync.source,
          })}
        </p>
        <p className="mb-0 text-muted">
          {t("company:companies.modal.deleteEmployees.syncBlocked.howTo", {
            removeButton: t("common:remove"),
            keepEmployees: t(
              sync.source === "GoogleWorkspace"
                ? "company:integrations.googleWorkspace.modal.delete.keepEmployees"
                : "company:integrations.activeDirectory.modal.delete.keepEmployees",
            ),
          })}
        </p>
        <Link to={integrationsPath} className="d-inline-block mt-3">
          <Button size="sm" variant="primary">
            {t("company:companies.modal.deleteEmployees.syncBlocked.openLink")}
          </Button>
        </Link>
      </>
    );
  });

const DeleteEmployeesButton: React.FC<{
  companyId: string;
  companyTitle: string;
}> = ({ companyId, companyTitle }) => {
  const { t } = useTranslation(["company", "common"]);

  const [visible, setVisible] = useToggle(false);
  const [sync, setSync] = useState<ActiveSync | null>(null);

  const [loadCount, { data: countData, loading: countLoading }] = useLazyQuery<{
    employeesCount: number;
  }>(QUERY_EMPLOYEES_COUNT, { fetchPolicy: "network-only" });

  const [onDelete, { loading: deleting }] = useMutation(
    MUTATION_DELETE_COMPANY_EMPLOYEES,
  );

  const employeeCount = countData?.employeesCount ?? 0;

  const onOpen = useCallback(() => {
    setSync(null);
    setVisible(true);

    loadCount({
      variables: { filter: { company: { id: { EQ: companyId } } } },
    });
  }, [companyId, loadCount, setVisible]);

  const onConfirm = useCallback(() => {
    return onDelete({ variables: { input: { company: companyId } } })
      .then(() => {
        toast.success<string>(
          t("company:companies.toast.deleteEmployees.started"),
        );
        setVisible(false);
      })
      .catch((error) => {
        const graphQLError = error?.graphQLErrors?.[0];

        /**
         * The server refuses while a sync exists and hands back which one, so the
         * modal can name it and link to the right integration page.
         */
        if (
          graphQLError?.extensions?.code === "COMPANY_HAS_ACTIVE_SYNC" &&
          graphQLError?.extensions?.source
        ) {
          setSync({ source: graphQLError.extensions.source });

          return;
        }

        toast.error<string>(
          error?.networkError?.result?.errors?.[0]?.message ??
            error?.message ??
            t("company:companies.toast.deleteEmployees.error"),
        );
      });
  }, [companyId, onDelete, setVisible, t]);

  const confirmDisabled = deleting || countLoading || employeeCount === 0;

  const renderBody = () => {
    if (sync) {
      return <SyncBlockedBody companyId={companyId} sync={sync} />;
    }

    if (countLoading) {
      return (
        <p className="mb-0">
          {t("company:companies.modal.deleteEmployees.loading")}
        </p>
      );
    }

    if (employeeCount === 0) {
      return (
        <p className="mb-0">
          {t("company:companies.modal.deleteEmployees.empty")}
        </p>
      );
    }

    return (
      <p className="mb-0">
        {t("company:companies.modal.deleteEmployees.body", {
          count: employeeCount,
          title: companyTitle,
        })}
      </p>
    );
  };

  return (
    <>
      <Button size="sm" variant="danger" className="mr-2" onClick={onOpen}>
        {t("company:companies.button.deleteEmployees")}
      </Button>
      <Modal
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="delete-company-employees-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="delete-company-employees-title">
            {sync
              ? t("company:companies.modal.deleteEmployees.syncBlocked.title")
              : t("company:companies.modal.deleteEmployees.title", {
                  title: companyTitle,
                })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderBody()}</Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {sync ? t("common:close") : t("common:cancel")}
          </Button>
          {!sync && (
            <Button
              size="sm"
              variant="danger"
              onClick={onConfirm}
              disabled={confirmDisabled}
            >
              {t("company:companies.button.deleteEmployeesConfirm", {
                count: employeeCount,
              })}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default React.memo(DeleteEmployeesButton);
